import { useState, useEffect, useMemo, useRef } from 'react';
import { activityDaysApi, usersApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Clock, User, Save, AlertCircle, Sparkles } from 'lucide-react';

function _minutesBetween(startStr, endStr) {
    if (!startStr || !endStr) return 0;
    try {
        const [sh, sm] = startStr.split(':').map(Number);
        const [eh, em] = endStr.split(':').map(Number);
        let diff = (eh * 60 + em) - (sh * 60 + sm);
        if (diff < 0) diff += 24 * 60; // cross-midnight
        return diff;
    } catch { return 0; }
}

export default function AgentHoursManager({
    caseId,
    dayId,
    assignedAgents,
    agentSchedules = {},
    dayDate = '',
    existingHours,
    serviceStart = '',
    serviceEnd = '',
    transferTimeMinutes = 0,
    onUpdate,
}) {
    const [agents, setAgents] = useState([]);
    const [agentHours, setAgentHours] = useState({});
    const [autoFilledIds, setAutoFilledIds] = useState(new Set()); // tiene traccia di chi è stato auto-compilato
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Ore totali calcolate dalla giornata (servizio + trasferimento)
    const autoHours = useMemo(() => {
        const svcMin = _minutesBetween(serviceStart, serviceEnd);
        const totalMin = svcMin + (parseInt(transferTimeMinutes, 10) || 0);
        return totalMin > 0 ? Math.round((totalMin / 60) * 100) / 100 : 0;
    }, [serviceStart, serviceEnd, transferTimeMinutes]);

    useEffect(() => {
        loadAgents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignedAgents, agentSchedules, dayDate]);

    // Ref per evitare auto-save ripetuti per la stessa giornata
    const autoSavedRef = useRef(null);

    useEffect(() => {
        // Reset auto-save quando cambia la giornata
        autoSavedRef.current = null;
    }, [dayId]);

    useEffect(() => {
        // Inizializza le ore: se esistono valori salvati non-zero li uso,
        // altrimenti auto-compilo con autoHours (servizio + trasferimento)
        if (!agents.length) return;
        setAgentHours(prev => {
            const hoursMap = { ...prev };
            const autoSet = new Set(autoFilledIds);
            const existingMap = {};
            (existingHours || []).forEach(ah => { existingMap[ah.agent_id] = ah.hours; });

            agents.forEach(agent => {
                const existingVal = existingMap[agent.id];
                const currentVal = hoursMap[agent.id];
                const existingValid = typeof existingVal === 'number' && existingVal > 0;

                if (existingValid) {
                    // Priorità al valore salvato nel DB (non auto-compilato)
                    hoursMap[agent.id] = existingVal;
                    autoSet.delete(agent.id);
                } else if (currentVal === undefined || currentVal === 0 || autoSet.has(agent.id)) {
                    // Campo vuoto o precedentemente auto-compilato → riapplica autoHours
                    if (autoHours > 0) {
                        hoursMap[agent.id] = autoHours;
                        autoSet.add(agent.id);
                    }
                }
            });
            setAutoFilledIds(autoSet);
            return hoursMap;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingHours, agents, autoHours]);

    // Auto-save: quando apriamo una giornata e alcuni agenti schedulati non hanno ore
    // ancora salvate (existing = 0 o mancante), salviamo subito le ore auto-calcolate
    // nel DB così il Riepilogo Ore Agenti riflette i totali senza click manuale.
    useEffect(() => {
        if (!agents.length || autoHours <= 0 || !dayId || !caseId) return;
        if (autoSavedRef.current === dayId) return; // già auto-salvato per questa giornata

        const existingMap = {};
        (existingHours || []).forEach(ah => { existingMap[ah.agent_id] = ah.hours; });

        // Agenti che riceverebbero l'auto-fill (esistente 0/mancante)
        const toAutofill = agents.filter(a => {
            const v = existingMap[a.id];
            return !(typeof v === 'number' && v > 0);
        });
        if (toAutofill.length === 0) {
            autoSavedRef.current = dayId; // niente da fare, marca come fatto
            return;
        }

        autoSavedRef.current = dayId;

        const payload = agents.map(a => {
            const existingVal = existingMap[a.id];
            const hoursVal = (typeof existingVal === 'number' && existingVal > 0)
                ? existingVal
                : autoHours;
            return {
                agent_id: a.id,
                agent_name: a.full_name,
                hours: hoursVal,
            };
        });

        activityDaysApi.updateAgentHours(caseId, dayId, payload)
            .then(() => {
                if (onUpdate) onUpdate();
            })
            .catch((err) => {
                // Non blocchiamo l'UX: log + permetti salvataggio manuale
                console.error('Auto-save ore agenti fallito:', err);
                autoSavedRef.current = null;
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agents, autoHours, existingHours, dayId, caseId]);

    const loadAgents = async () => {
        try {
            setLoading(true);
            const response = await usersApi.listAgents();
            // Filtra solo gli agenti assegnati al caso
            let assignedAgentsList = response.data.filter(
                agent => assignedAgents.includes(agent.id)
            );

            // Filtra per quelli schedulati in questa data specifica
            const hasAnySchedule = Object.keys(agentSchedules || {}).length > 0;
            if (dayDate && hasAnySchedule) {
                // Schedules configurati → mostra SOLO agenti schedulati quel giorno
                assignedAgentsList = assignedAgentsList.filter(agent => {
                    const dates = agentSchedules?.[agent.id]?.dates || [];
                    return dates.includes(dayDate);
                });
            }
            // Fallback: se non ci sono schedules (casi vecchi), mostra tutti gli assegnati

            setAgents(assignedAgentsList);
        } catch (err) {
            console.error('Errore caricamento agenti:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleHoursChange = (agentId, value) => {
        const hours = parseFloat(value) || 0;
        setAgentHours(prev => ({
            ...prev,
            [agentId]: hours
        }));
        // L'utente ha modificato manualmente → non è più auto-compilato
        setAutoFilledIds(prev => {
            const next = new Set(prev);
            next.delete(agentId);
            return next;
        });
    };

    const handleReapplyAuto = () => {
        if (autoHours <= 0) return;
        const next = {};
        const autoSet = new Set();
        agents.forEach(a => {
            next[a.id] = autoHours;
            autoSet.add(a.id);
        });
        setAgentHours(next);
        setAutoFilledIds(autoSet);
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');
            setSuccess('');

            // Prepara i dati delle ore
            const hoursData = agents.map(agent => ({
                agent_id: agent.id,
                agent_name: agent.full_name,
                hours: agentHours[agent.id] || 0
            }));

            await activityDaysApi.updateAgentHours(
                caseId, 
                dayId, 
                hoursData
            );

            setSuccess('Ore salvate correttamente');
            
            if (onUpdate) {
                onUpdate();
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nel salvataggio');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-4 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-[#00FF41]/30 border-t-[#00FF41] rounded-full animate-spin" />
            </div>
        );
    }

    if (agents.length === 0) {
        const hasAnySchedule = Object.keys(agentSchedules || {}).length > 0;
        return (
            <div className="bg-[#0A0A0A] border border-white/10 p-4 text-center">
                <p className="text-white/40 text-sm">
                    {hasAnySchedule && dayDate
                        ? 'Nessun agente schedulato per questa giornata'
                        : 'Nessun agente assegnato a questo caso'}
                </p>
            </div>
        );
    }

    return (
        <div className="bg-[#0A0A0A] border border-white/10 p-4 space-y-4 overflow-hidden">
            <h4 className="font-heading text-white flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#00FF41]" />
                Ore Lavorate per Agente
            </h4>

            {/* Banner auto-calcolo */}
            {autoHours > 0 && (
                <div className="bg-[#00FF41]/5 border border-[#00FF41]/30 p-2.5 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <Sparkles className="w-4 h-4 text-[#00FF41] flex-shrink-0" />
                        <span className="text-white/80 text-xs">
                            Calcolo automatico: <span className="font-mono text-[#00FF41]">{autoHours.toFixed(2)} h</span> (servizio + trasferimento)
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={handleReapplyAuto}
                        className="text-[#00FF41] text-xs font-mono uppercase hover:underline flex-shrink-0"
                        data-testid="reapply-auto-hours-btn"
                        title="Ricalcola tutte le ore su questo valore"
                    >
                        Ricalcola
                    </button>
                </div>
            )}

            {error && (
                <div className="bg-red-500/10 border border-red-500 p-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                    <span className="text-red-500 text-sm">{error}</span>
                </div>
            )}

            {success && (
                <div className="bg-green-500/10 border border-green-500 p-2">
                    <span className="text-green-500 text-sm">{success}</span>
                </div>
            )}

            <div className="space-y-3">
                {agents.map(agent => (
                    <div 
                        key={agent.id} 
                        className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 bg-black/30 border border-white/5"
                    >
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                            <User className="w-4 h-4 text-[#00FF41] flex-shrink-0" />
                            <span className="text-white text-sm truncate">{agent.full_name}</span>
                            {autoFilledIds.has(agent.id) && (
                                <span
                                    className="text-[10px] font-mono uppercase px-1.5 py-0.5 bg-[#00FF41]/10 text-[#00FF41] border border-[#00FF41]/30"
                                    title="Valore calcolato automaticamente (servizio + trasferimento)"
                                >
                                    auto
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2 justify-end sm:justify-start flex-shrink-0">
                            <Input
                                type="number"
                                min="0"
                                max="24"
                                step="0.5"
                                value={agentHours[agent.id] || ''}
                                onChange={(e) => handleHoursChange(agent.id, e.target.value)}
                                placeholder="0"
                                data-testid={`agent-hours-${agent.id}`}
                                className="w-20 bg-black border-white/20 text-white text-center focus:border-[#00FF41] rounded-none"
                            />
                            <span className="text-white/40 text-sm">ore</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex justify-end pt-3 border-t border-white/10">
                <Button
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="save-agent-hours-btn"
                    className="bg-[#00FF41] text-black hover:bg-[#00CC33] rounded-none"
                >
                    <Save className="w-4 h-4 mr-2" />
                    {saving ? 'SALVATAGGIO...' : 'SALVA ORE'}
                </Button>
            </div>
        </div>
    );
}
